import React, { useState, useEffect } from 'react';
import { X, CheckCircle, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface NewsletterPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NewsletterPopup: React.FC<NewsletterPopupProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    
    try {
      // First, add to database
      const { error: dbError } = await supabase
        .from('newsletter_subscribers')
        .insert([
          {
            email: email.toLowerCase(),
            name: name || null,
            confirmed_at: new Date().toISOString(),
            is_active: true
          }
        ]);

      if (dbError) {
        // If email already exists, that's still a success
        if (dbError.code !== '23505') {
          throw dbError;
        }
      }

      // Then send welcome email
      const { error: emailError } = await supabase.functions.invoke('send-newsletter', {
        body: {
          email,
          name,
          type: 'subscribe'
        }
      });

      if (emailError) {
        console.error('Email error:', emailError);
        // Don't throw - database subscription is more important
      }

      setIsSuccess(true);
      toast({
        title: "Inschrijving Geslaagd! 🎉",
        description: "Check je e-mail voor een welkomstbericht.",
      });

      // Auto-close after 3 seconds
      setTimeout(() => {
        onClose();
        setIsSuccess(false);
        setEmail('');
        setName('');
      }, 3000);

    } catch (error: any) {
      console.error('Newsletter subscription error:', error);
      toast({
        title: "Oeps, er ging iets mis",
        description: "Probeer het later opnieuw of neem contact op.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-xl max-w-md w-full shadow-2xl border">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground p-6 rounded-t-xl relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-primary-foreground/80 hover:text-primary-foreground transition-colors"
          >
            <X size={20} />
          </button>
          
          <div className="flex items-center gap-3 mb-2">
            <Mail className="text-primary-foreground" size={24} />
            <h2 className="text-xl font-bold">Mis Niets van de Smart Home Revolutie!</h2>
          </div>
          
          <p className="text-primary-foreground/90 text-sm">
            Blijf de toekomst een stap voor. Schrijf je in voor de SOL-VI nieuwsbrief.
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {isSuccess ? (
            <div className="text-center">
              <CheckCircle className="mx-auto mb-4 text-green-500" size={48} />
              <h3 className="text-lg font-semibold mb-2">Welkom bij SOL-VI! 🏠</h3>
              <p className="text-muted-foreground text-sm">
                Je ontvangt binnenkort een welkomstbericht in je mailbox.
              </p>
            </div>
          ) : (
            <>
              {/* Benefits */}
              <div className="mb-6">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  Wat krijg je?
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>Profiteer van de nieuwste technieken</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>Ontdek onze nieuwste acties</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>Krijg toegang tot hoge kortingen</span>
                  </div>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-1">
                    Naam (optioneel)
                  </label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Jouw naam"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-1">
                    E-mailadres *
                  </label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="jouw@email.nl"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
                  disabled={isSubmitting || !email}
                >
                  {isSubmitting ? 'Bezig...' : 'Ja, houd me op de hoogte!'}
                </Button>
              </form>

              {/* Disclaimer */}
              <p className="text-xs text-muted-foreground mt-4 text-center">
                Je kunt je op elk moment uitschrijven. We respecteren je privacy.{' '}
                <a href="/privacy" className="underline hover:no-underline">
                  Lees ons privacybeleid
                </a>.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Hook for managing newsletter popup timing
export const useNewsletterPopup = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  useEffect(() => {
    // Don't show if already shown in this session
    if (hasShown) return;
    
    // Don't show if user already dismissed it recently
    const dismissed = localStorage.getItem('newsletter-popup-dismissed');
    if (dismissed) {
      const dismissedTime = new Date(dismissed);
      const now = new Date();
      const daysDiff = (now.getTime() - dismissedTime.getTime()) / (1000 * 3600 * 24);
      
      // Only show again after 7 days
      if (daysDiff < 7) return;
    }

    let timeoutId: NodeJS.Timeout;
    let scrollHandler: () => void;
    let exitHandler: (e: MouseEvent) => void;

    // Show after 15 seconds
    timeoutId = setTimeout(() => {
      if (!hasShown) {
        setShowPopup(true);
        setHasShown(true);
      }
    }, 15000);

    // Show when 50% scrolled
    scrollHandler = () => {
      const scrollPercent = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
      if (scrollPercent > 50 && !hasShown) {
        setShowPopup(true);
        setHasShown(true);
      }
    };

    // Show on exit intent
    exitHandler = (e: MouseEvent) => {
      if (e.clientY <= 0 && !hasShown) {
        setShowPopup(true);
        setHasShown(true);
      }
    };

    window.addEventListener('scroll', scrollHandler);
    document.addEventListener('mouseleave', exitHandler);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('scroll', scrollHandler);
      document.removeEventListener('mouseleave', exitHandler);
    };
  }, [hasShown]);

  const closePopup = () => {
    setShowPopup(false);
    localStorage.setItem('newsletter-popup-dismissed', new Date().toISOString());
  };

  return { showPopup, closePopup };
};